import React from 'react';
import { holdCodeLabel } from '../lib/hold-codes.js';
import type { JnHoldResult } from '../lib/jn-hold.js';
import errorIcon from '../assets/error-icon.svg';

/**
 * Fraud-team-transfer warning (issue #669 — "Fraud and AML checks", @dylan219;
 * extended by #784 Option B).
 *
 * Raised when an account carries BOTH a 'J' hold ("Multiple Warnings") AND an
 * 'N' hold ("Pay No Withdrawal") simultaneously, OR any single 'Q'/'8'/'V' fraud
 * hold (@dylan219 #784, 2026-09-12 "go with option B please" — Q/V/8 now behaves
 * exactly like J&N, superseding CCF-43's earlier dismissible fraud pop-up). It
 * surfaces, for the agent:
 *   - both hold type(s) on the affected account(s), rendered as
 *     `<code> — <explanation>` via the Defined Account Holds catalogue
 *     (holdCodeLabel, issue #108);
 *   - a clear error message that the agent cannot proceed; and
 *   - the exact fraud-team transfer scripting (customer name interpolated).
 *
 * NO DISMISS AFFORDANCE (@dylan219, 2026-09-01: "remove this button and do not
 * allow the agent navigate off this Warning for J&N block only for now"): the
 * former "Acknowledge — cannot proceed" footer button is GONE — no acknowledge,
 * no close, no ×, nothing to click past. It is rendered by the APP SHELL
 * (App.tsx, via `jn-hold-block-store.ts`) IN PLACE OF the routed screens: while
 * the block is raised for the identified customer, EVERY navigation target is
 * absent from the DOM, so on any route (incl. deep links / programmatic
 * navigations) the agent only ever sees this warning and cannot move. The only
 * interactive surface outside the panel is the agent banner's server-side Clear
 * session — the sanctioned teardown the BFF's `requireNoJnHoldBlock` allow-list
 * deliberately keeps open (same "resolution stays reachable" precedent as the
 * CCF-13 freeze overlay, #506). The authoritative stop remains the server-side
 * `requireNoJnHoldBlock` guard (403 on all servicing/banking routes) — this
 * warning is the agent-facing layer, never the enforcement layer.
 *
 * Rendered as a page-level panel rather than a fixed full-viewport modal
 * precisely so the banner (Clear session) stays usable; the lock itself comes
 * from App not rendering anything else while blocked, not from a pointer trap.
 *
 * Visual treatment (error icon, red title, scripted-note styling) still mirrors
 * the CCF-43 auth-hold pop-up (`AuthHoldPopup`) so scripted fraud/AML warnings
 * stay consistent — but unlike that pop-up, this one is NOT dismissible.
 */
export function JnHoldPopup({
  result,
}: {
  result: JnHoldResult;
}): React.JSX.Element {
  return (
    <div
      role="alertdialog"
      aria-labelledby="jn-hold-title"
      data-testid="jn-hold-warning"
      className="flex items-start justify-center min-h-[60vh] p-6"
    >
      <div className="bg-white border-2 border-[#dad7d3] shadow-lg max-w-[720px] w-full max-h-[90vh] overflow-auto p-8">
        <div className="flex items-start gap-3 mb-6">
          <img
            src={errorIcon}
            alt=""
            aria-hidden="true"
            className="w-6 h-6 mt-1 shrink-0"
          />
          <h2
            id="jn-hold-title"
            className="text-[20px] font-['Arial',sans-serif] font-bold m-0 text-[#dc2626]"
          >
            Account status restricted — transfer to fraud team
          </h2>
        </div>

        {/* Clear error message: the agent cannot proceed. */}
        <p
          role="alert"
          className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] m-0 mb-6 leading-[1.5]"
        >
          This account has a fraud/AML hold in place that prevents servicing.
          You cannot proceed with servicing this customer — follow the scripting
          below and transfer the customer to the fraud team.
        </p>

        {/* Both hold type(s) per affected account. */}
        <section className="mb-6">
          <h3 className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 mb-2">
            Holds on this account
          </h3>
          <div className="space-y-4">
            {result.accounts.map((a) => (
              <div
                key={a.accountLast4}
                className="border border-[#dad7d3] p-4 bg-[#F8EEE3]"
              >
                <dl className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] grid grid-cols-[160px_1fr] gap-x-4 gap-y-1">
                  <dt className="font-bold">Account (last 4)</dt>
                  <dd className="m-0">{a.accountLast4}</dd>
                  <dt className="font-bold">Hold type(s)</dt>
                  <dd className="m-0">
                    <ul className="m-0 pl-0 list-none space-y-1">
                      {a.holdCodes.map((code) => (
                        <li key={code}>{holdCodeLabel(code)}</li>
                      ))}
                    </ul>
                  </dd>
                </dl>
              </div>
            ))}
          </div>
        </section>

        {/* Agent scripting (customer name interpolated). */}
        <section className="mb-6">
          <h3 className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 mb-2">
            Agent scripting
          </h3>
          <p
            role="note"
            className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] m-0 leading-[1.5] bg-white border-l-4 border-[#fc4c02] pl-3 py-2"
          >
            {result.scripting}
          </p>
        </section>

        {/* Deliberately NO footer: no Acknowledge, no Close, no × — the agent
            cannot navigate off this warning while the J&N block is up
            (@dylan219, 2026-09-01). */}
      </div>
    </div>
  );
}
